import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import leaderboardApi, { type LeaderboardEntry } from '../../services/leaderboardApi';
import './LeaderboardPage.css';

type GameType = 'overall' | 'kadi-ni-tidi' | 'teen-patti' | 'rummy';
type Period = 'daily' | 'weekly' | 'monthly' | 'all-time';

const LeaderboardPage: React.FC = () => {
  const { user } = useAuth();
  const [gameType, setGameType] = useState<GameType>('overall');
  const [period, setPeriod] = useState<Period>('all-time');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, [gameType, period]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const leaderboardData = await leaderboardApi.getTopPlayers(gameType, period, 100);
      setEntries(leaderboardData);

      // Get user's rank if logged in
      if (user?.id) {
        const rankInfo = await leaderboardApi.getUserRank(user.id, gameType, period);
        setUserRank(rankInfo.rank);
      }
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getGameTypeName = (type: GameType): string => {
    const names: Record<GameType, string> = {
      'overall': 'Overall',
      'kadi-ni-tidi': 'Kadi ni Tidi',
      'teen-patti': 'Teen Patti',
      'rummy': 'Rummy',
    };
    return names[type];
  };

  const getPeriodName = (p: Period): string => {
    const names: Record<Period, string> = {
      'daily': 'Today',
      'weekly': 'This Week',
      'monthly': 'This Month',
      'all-time': 'All Time',
    };
    return names[p];
  };

  const getRankIcon = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getStreakBadge = (streak: number): string | null => {
    if (streak >= 10) return '🔥';
    if (streak >= 5) return '⚡';
    if (streak >= 3) return '✨';
    return null;
  };

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">
        {/* Header */}
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">🏆 Leaderboard</h1>
          <p className="leaderboard-subtitle">Compete with players worldwide</p>
        </div>

        {/* Game Type Tabs */}
        <div className="game-type-tabs">
          {(['overall', 'kadi-ni-tidi', 'teen-patti', 'rummy'] as GameType[]).map((type) => (
            <button
              key={type}
              className={`game-type-tab ${gameType === type ? 'active' : ''}`}
              onClick={() => setGameType(type)}
            >
              {getGameTypeName(type)}
            </button>
          ))}
        </div>

        {/* Period Tabs */}
        <div className="period-tabs">
          {(['daily', 'weekly', 'monthly', 'all-time'] as Period[]).map((p) => (
            <button
              key={p}
              className={`period-tab ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {getPeriodName(p)}
            </button>
          ))}
        </div>

        {/* User's Rank */}
        {user && userRank > 0 && (
          <div className="user-rank-banner">
            <span className="user-rank-label">Your Rank:</span>
            <span className="user-rank-value">{getRankIcon(userRank)}</span>
            <span className="user-rank-text">
              {userRank > 100 ? `${userRank} / ${entries.length}+` : `Top ${userRank}`}
            </span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="leaderboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading leaderboard...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="leaderboard-error">
            <p>{error}</p>
            <button onClick={loadLeaderboard} className="retry-button">
              Retry
            </button>
          </div>
        )}

        {/* Leaderboard Content */}
        {!loading && !error && (
          <>
            {/* Top 3 Podium */}
            {entries.length >= 3 && (
              <div className="podium-container">
                {/* 2nd Place */}
                <div className="podium-player podium-2nd">
                  <div className="podium-rank">🥈</div>
                  <div className="podium-avatar">
                    {entries[1].userId.avatar ? (
                      <img src={entries[1].userId.avatar} alt={entries[1].userId.username} />
                    ) : (
                      <div className="avatar-placeholder">
                        {entries[1].userId.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="podium-name">{entries[1].userId.displayName || entries[1].userId.username}</div>
                  <div className="podium-points">{entries[1].points.toLocaleString()} pts</div>
                  <div className="podium-stats">
                    {entries[1].wins}W · {entries[1].winRate.toFixed(1)}%
                    {getStreakBadge(entries[1].currentWinStreak) && (
                      <span className="streak-badge">{getStreakBadge(entries[1].currentWinStreak)}{entries[1].currentWinStreak}</span>
                    )}
                  </div>
                </div>

                {/* 1st Place */}
                <div className="podium-player podium-1st">
                  <div className="podium-rank">🥇</div>
                  <div className="podium-avatar champion">
                    {entries[0].userId.avatar ? (
                      <img src={entries[0].userId.avatar} alt={entries[0].userId.username} />
                    ) : (
                      <div className="avatar-placeholder">
                        {entries[0].userId.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="podium-name">{entries[0].userId.displayName || entries[0].userId.username}</div>
                  <div className="podium-points">{entries[0].points.toLocaleString()} pts</div>
                  <div className="podium-stats">
                    {entries[0].wins}W · {entries[0].winRate.toFixed(1)}%
                    {getStreakBadge(entries[0].currentWinStreak) && (
                      <span className="streak-badge">{getStreakBadge(entries[0].currentWinStreak)}{entries[0].currentWinStreak}</span>
                    )}
                  </div>
                </div>

                {/* 3rd Place */}
                <div className="podium-player podium-3rd">
                  <div className="podium-rank">🥉</div>
                  <div className="podium-avatar">
                    {entries[2].userId.avatar ? (
                      <img src={entries[2].userId.avatar} alt={entries[2].userId.username} />
                    ) : (
                      <div className="avatar-placeholder">
                        {entries[2].userId.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="podium-name">{entries[2].userId.displayName || entries[2].userId.username}</div>
                  <div className="podium-points">{entries[2].points.toLocaleString()} pts</div>
                  <div className="podium-stats">
                    {entries[2].wins}W · {entries[2].winRate.toFixed(1)}%
                    {getStreakBadge(entries[2].currentWinStreak) && (
                      <span className="streak-badge">{getStreakBadge(entries[2].currentWinStreak)}{entries[2].currentWinStreak}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Ranks 4-100 */}
            <div className="leaderboard-list">
              {entries.slice(3).map((entry, index) => {
                const rank = index + 4;
                const isCurrentUser = user?.id === entry.userId._id;

                return (
                  <div
                    key={entry._id}
                    className={`leaderboard-row ${isCurrentUser ? 'current-user' : ''}`}
                  >
                    <div className="rank-column">
                      <span className="rank-number">#{rank}</span>
                    </div>

                    <div className="player-column">
                      <div className="player-avatar">
                        {entry.userId.avatar ? (
                          <img src={entry.userId.avatar} alt={entry.userId.username} />
                        ) : (
                          <div className="avatar-placeholder">
                            {entry.userId.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="player-info">
                        <div className="player-name">
                          {entry.userId.displayName || entry.userId.username}
                          {isCurrentUser && <span className="you-badge">You</span>}
                        </div>
                        <div className="player-level">Level {entry.userId.level}</div>
                      </div>
                    </div>

                    <div className="stats-column">
                      <div className="stat-item">
                        <span className="stat-label">Points</span>
                        <span className="stat-value">{entry.points.toLocaleString()}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Win Rate</span>
                        <span className="stat-value">{entry.winRate.toFixed(1)}%</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Games</span>
                        <span className="stat-value">{entry.gamesPlayed}</span>
                      </div>
                      {entry.currentWinStreak > 0 && (
                        <div className="stat-item streak">
                          <span className="stat-label">Streak</span>
                          <span className="stat-value">
                            {getStreakBadge(entry.currentWinStreak)}{entry.currentWinStreak}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {entries.length === 0 && (
              <div className="leaderboard-empty">
                <p>No players on this leaderboard yet.</p>
                <p>Be the first to play and claim the top spot!</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
